import React, { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'

const Login: React.FC = () => {
  const { signInWithGoogle } = useAuth()
  const [searchParams] = useSearchParams()
  const [isLoading, setIsLoading] = useState(false)

  const flowParam = searchParams.get('flow')
  const flow = flowParam === 'provider' ? 'provider' : 'merchant'
  const isProvider = flow === 'provider'

  const handleGoogleLogin = async () => {
    try {
      setIsLoading(true)
      await signInWithGoogle(flow)
    } catch (error: any) {
      console.error('Login error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <motion.div
        className="w-full max-w-md"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        {/* Glass card */}
        <div className="glass-card-strong rounded-2xl p-10 relative overflow-hidden">
          {/* Orange top glow */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(ellipse at top, rgba(249,115,22,0.12) 0%, transparent 60%)',
              pointerEvents: 'none',
            }}
          />
          {/* Orange top border line */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: '25%',
              right: '25%',
              height: 1,
              background: 'linear-gradient(to right, transparent, rgba(249,115,22,0.6), transparent)',
            }}
          />

          {/* Content */}
          <div className="relative">
            {/* Logo + wordmark */}
            <div className="flex flex-col items-center mb-8">
              <img src="/logo.svg" alt="FLOWSTOCK" className="h-10 w-auto mb-3" />
              <h1 className="font-heading font-bold text-white text-2xl tracking-tight">
                FLOWSTOCK
              </h1>
              <span className="text-orange-400/60 text-xs uppercase tracking-widest mt-1">
                {isProvider ? 'Provider Portal' : 'Merchant Portal'}
              </span>
            </div>

            {/* Heading */}
            <div className="mb-8 text-center">
              <h2 className="font-heading font-bold text-white text-xl mb-1">
                Welcome back
              </h2>
              <p className="text-white/40 text-sm">
                {isProvider
                  ? 'Sign in to manage your product catalog'
                  : 'Sign in to manage your shop'}
              </p>
            </div>

            {/* Google button */}
            <button
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-3 bg-orange-500 hover:bg-orange-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold py-3.5 px-6 rounded-full transition-colors text-sm"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24">
                  <path fill="#EA4335" d="M5.266 9.765A7.077 7.077 0 0 1 12 4.909c1.69 0 3.218.6 4.418 1.582L19.91 3C17.782 1.145 15.055 0 12 0 7.27 0 3.198 2.698 1.24 6.65l4.026 3.115Z" />
                  <path fill="#34A853" d="M16.04 18.013c-1.09.703-2.474 1.078-4.04 1.078a7.077 7.077 0 0 1-6.723-4.823l-4.04 3.067A11.965 11.965 0 0 0 12 24c2.933 0 5.735-1.043 7.834-3l-3.793-2.987Z" />
                  <path fill="#4A90E2" d="M19.834 21c2.195-2.048 3.62-5.096 3.62-9 0-.71-.109-1.473-.272-2.182H12v4.637h6.436c-.317 1.559-1.17 2.766-2.395 3.558L19.834 21Z" />
                  <path fill="#FBBC05" d="M5.277 14.268A7.12 7.12 0 0 1 4.909 12c0-.782.125-1.533.357-2.235L1.24 6.65A11.934 11.934 0 0 0 0 12c0 1.92.445 3.73 1.237 5.335l4.04-3.067Z" />
                </svg>
              )}
              <span>{isLoading ? 'Signing in...' : 'Continue with Google'}</span>
            </button>

            {/* Flow toggle */}
            <div className="mt-6 text-center">
              {isProvider ? (
                <Link
                  to="/login"
                  className="text-white/30 hover:text-white/60 text-xs transition-colors"
                >
                  Sign in as Merchant →
                </Link>
              ) : (
                <Link
                  to="/login?flow=provider"
                  className="text-white/30 hover:text-white/60 text-xs transition-colors"
                >
                  Sign in as Provider →
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-white/20 text-xs mt-6">
          Powered by FLOWSTOCK · ByteHouse LLC
        </p>
      </motion.div>
    </div>
  )
}

export default Login
